import { Controller } from '@hotwired/stimulus'

import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Typography from '@tiptap/extension-typography'
import Youtube from '@tiptap/extension-youtube'
import { markInputRule } from '@tiptap/core'

import Compress from 'compress.js'

import swal from 'sweetalert'
const mime = require('mime')

export default class extends Controller {
  static values = {
    postId: Number,
    type: String,
    cdnDomain: String
  }

  static targets = [
    'editor',
    'textInput',
    'imageInput',
    'attachEmbedLink',
    'poll',
    'submit'
  ]

  connect () {
    this.initTiptap()
    this.processEditingPost()
  }

  disconnect() {
    this.editor.destroy()
  }

  initTiptap() {
    this.editor = new Editor({
      element: this.editorTarget,
      extensions: [
        StarterKit.configure({
          orderedList: false,
          bulletList: false
        }),
        Placeholder,
        Image,
        Link.configure({
          autolink: false,
          protocols: ['http', 'https'],
          inclusive: false,
          exitable: true,
        }).extend({
          addInputRules() {
            return [
              markInputRule({
                find: /(https?:\/\/[^\s]+)/g,
                type: this.type,
                getAttributes: (match) => ({ href: match[1] }),
              }),
            ]
          },
        }),
        Typography,
        Youtube,
      ],
      editorProps: {
        transformPastedHTML(html) {
          var div = document.createElement('div')
          div.innerHTML = html
          return div.textContent || div.innerText || ''
        },
      },
    })

    this.editor.on('update', ({ editor }) => {
      this.textInputTarget.value = editor.getHTML()

      editor.on('update', ({ editor }) => {
        const state = editor.state
        const { from } = state.selection

        // Get the current content as plain text
        const content = editor.getText()

        // Check if the last three characters are "AAA"
        if (content.endsWith('ФФФФ')) {
          const tr = editor.state.tr
          const start = from - 4 // Calculate the starting position of "AAA"
          const end = from       // Calculate the ending position of "AAA"

          // The Unicode symbol to replace "AAA" with
          const replacementChar = '\u0424\u0301'

          // Replace "AAA" with "Ф́"
          tr.insertText(replacementChar, start, end)

          // Apply bold and italic marks to "Ф́"
          tr.addMark(start, start + 2, state.schema.marks.bold.create())
          tr.addMark(start, start + 2, state.schema.marks.italic.create())

          // Set the selection after "Ф́" without any marks
          tr.setSelection(state.selection.constructor.near(tr.doc.resolve(start + 2)))
          tr.removeStoredMark(state.schema.marks.bold)
          tr.removeStoredMark(state.schema.marks.italic)

          editor.view.dispatch(tr)
        }
      }
    )})
  }

  processEditingPost() {
    if (this.typeValue == 'edit') {
      this.editor.commands.setContent(this.textInputTarget.value)
    } else {
      this.textInputTarget.value = ''
    }
  }

  processImagePasteEvent(event) {
    const items = (event.clipboardData || event.originalEvent.clipboardData).items

    for (const item of items) {
      const allowed = ['image/gif', 'image/jpeg', 'image/png', 'image/webp']

      if (item.kind === "file" && allowed.includes(item.type)) {
        let tmp_file = item.getAsFile()

        tmp_file.arrayBuffer().then(buffer => {
          let file = new File([buffer], `pasted_${Date.now()}.${mime.getExtension(tmp_file.type)}`)
          this.compressAndUploadImage(file)
        }).catch(e => console.log(e))

        break
      }
    }
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run()
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run()
  }

  toggleStrike() {
    this.editor.chain().focus().toggleStrike().run()
  }

  toggleQuote() {
    this.editor.chain().focus().toggleBlockquote().run()
  }

  openAttachImage() {
    this.imageInputTarget.click()
  }

  submit() {
    this.submitTarget.click()
  }

  processImageInputChange() {
    const file = this.imageInputTarget.files[0]

    // Ensure only images are uploaded
    if (/^image\//.test(file.type)) {
      this.compressAndUploadImage(file)
    } else {
      swal('Только картинки, пожалуйста')
    }
  }

  compressAndUploadImage(file) {
    const compress = new Compress()

    compress.compress([file], {
      size: 1, // the max size in MB, defaults to 2MB
      quality: .8, // the quality of the image, max is 1,
      maxWidth: 1200, // the max width of the output image, defaults to 1920px
      maxHeight: 1200, // the max height of the output image, defaults to 1920px
      resize: true, // defaults to true, set false if you do not want to resize the image width and height
      rotate: false, // See the rotation section below
    }).then((data) => {
      const base64str = data[0].data
      const imgExt = data[0].ext
      const blob = Compress.convertBase64ToFile(base64str, imgExt)
      const compressed = new File([blob], `image_${Date.now()}.${mime.getExtension(file.type)}`)

      this.uploadImage(compressed)
    })
  }

  uploadImage(file) {
    const formData = new FormData()
    formData.append('file', file)

    fetch('/uploads', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(blob => {
      this.insertImage(
        `https://${this.cdnDomainValue}/${blob.key}`
      );
    })
    .catch(error => {
      console.log(error);
    })
  }

  insertImage(fileUrl) {
    if (this.editor && !this.editor.isDestroyed) {
      this.editor.chain().focus().setImage({ src: fileUrl }).run()
    }
  }
}
